<template>
  <element>
    <AppBar :title="$t('dashboard.title')" :class="{ 'blur' : menuVisible }" />
    <v-container :class="{ 'blur' : menuVisible }">
      <v-menu
        v-model="calendarVisible"
        :close-on-content-click="false"
        max-width=290
        max-height=380
        offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn 
            color="#033e8c"
            v-bind="attrs"
            v-on="on"
            dark
            rounded
            left>
            <v-icon left>mdi-calendar-month</v-icon>
            {{ dateRangeText }}
          </v-btn>
        </template>
        <v-date-picker 
          v-model="dates" 
          range color="#033e8c"
          :locale="$i18n.locale">
        </v-date-picker>
      </v-menu>
      <v-row v-if="isLoadingMetrics" no-gutters class="mt-3">
        <v-col>
          <v-progress-circular 
            indeterminate
            :size="70"
            :width="7"
            color="#033e8c"
          ></v-progress-circular>
        </v-col>
      </v-row>
      <element v-else>
        <h5 v-if="!plausible" class="chart-label text-center mt-3">{{ $t('dashboard.noData') }}</h5>
        <element v-else>
          <h5 class="chart-label text-center mt-3">{{ $t('dashboard.donutChart.title') }}</h5>
          <DoughnutChart
            class="char-status" 
            :chartdata="donutChartData" 
            :options="donutChartOptions"
          ></DoughnutChart>
          <h5 class="chart-label text-center mt-3">{{ $t('dashboard.lineChart.title') }}</h5>
          <LineChart
            :data="lineChartData"
            :options="lineChartOptions"
            height=500
            class="chart-statistics"
          ></LineChart>
        </element>
      </element>
      <v-row no-gutters class="mt-3 num-metrics">
        <v-col class="pr-2">
          <NumericIndicator :value="carsRegistered" :description="$t('dashboard.metrics.cars.new')" />
        </v-col>
        <v-col class="pl-2">
          <NumericIndicator :value="carsWaitingProcessing" :description="$t('dashboard.metrics.cars.waiting_processing')" />
        </v-col>
        <v-col class="pl-2">
          <NumericIndicator :value="carsProcessed" :description="$t('dashboard.metrics.cars.processed')" />
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-5">
        <span class="table-badge bg-success text-white">{{ $t('dashboard.processTable.badge') }}</span>
      </v-row>
      <ProcessTable class="mt-3" />
    </v-container>
    <v-menu 
      top 
      offset-y
      :max-width="maxWidth"
      :max-height="maxHeight"
      eager>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-click-outside="hideProcessMenu"
          color="#033e8c"
          fixed
          fab
          dark
          bottom
          right
          v-bind="attrs"
          v-on="on"
          @click="showOrHideProcessMenu"
        >
          <v-icon :class="menuVisible ? 'btn-animation' : ''" dark>mdi-plus</v-icon>
        </v-btn>
      </template>

      <div class="d-flex align-items-end w-100 h-100 bottom-right">
        <ul>
          <li
            v-for="process in processList"
            :key="process.key">
              <ProcessMenuItem :definitionKey="process.key" :name="process.name" @click="startNewProcess(process.key)" />
          </li>
        </ul>
        <div
          v-if="Object.keys(processList).length === 0"
          no-gutters
          class="d-flex flex-row justify-content-end">
          <h5 class="text-right process-name mb-0 mr-3 align-self-center">{{ $t('common.loading') }}</h5>
          <v-progress-circular 
            indeterminate
            class="my-4 mr-3 align-self-center"
            color="#033e8c">
          </v-progress-circular>
        </div>
      </div>
    </v-menu>
  </element>
</template>

<script>
import AppBar from "@/components/common/AppBar.vue"
import DoughnutChart from "@/components/dashboard/DoughnutChart.vue"
import LineChart from "@/components/dashboard/LineChart.vue"
import ProcessMenuItem from "@/components/process_menu/MenuItem.vue"
import ProcessTable from "@/components/dashboard/ProcessTable.vue"
import NumericIndicator from "@/components/dashboard/NumericIndicator.vue"

export default {
  name: "DashboardPage",
  props: {},
  components: {
    AppBar,
    DoughnutChart,
    LineChart,
    ProcessMenuItem,
    ProcessTable,
    NumericIndicator
  },
  data () {
    return {
      calendarVisible: false,
      dates: [],
      carStatus: [],
      menuVisible: false,
      previousScrollY: 0,
      donutChartOptions: {
        responsive: true,
        borderWidth: "10px",
        hoverBackgroundColor: "red",
        hoverBorderWidth: "10px",
        cutoutPercentage: 70,
        title: {
          display: false,
          position: "top",
          text: this.$t('dashboard.donutChart.title'),
          fontSize: 18,
          fontColor: "#d2d2d2"
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            padding: 25,
            boxWidth: 10,
            fontColor: "#333",
            fontSize: 16,
            usePointStyle: true,
          }
        },
        elements: {
          center: {
            text: "17.5",
            color: "#2e384d",
            fontStyle: "Rubik",
            sidePadding: 20,
            minFontSize: 20,
            lineHeight: 25
          },        
        },
      },
      stepSize: 1,
      isLoadingMetrics: true,
      plausible: false,
      carsRegistered: 0,
      carsWaitingProcessing: 0,
      carsProcessed: 0
    }
  },
  computed: {
    processList: function () {
      return this.$store.getters.getDeployedProcessList || {}
    },
    maxWidth: function () {
      return window.screen.innerWidth
    },
    maxHeight: function () {
      return window.screen.innerHeight
    },
    dateRangeText: function () {
      return this.dates.join(' ~ ')
    },
    donutChartData: function () {
      return {
        datasets: [{
            data: this.carCountsByCategory(),
            backgroundColor: [
              '#033e8c',
              'Green',
              '#d9753b'
            ],
        }],
        // These labels appear in the legend and in the tooltips when hovering different arcs
        labels: [
          this.$t('dashboard.category.new'),
          this.$t('dashboard.category.processing'),
          this.$t('dashboard.category.processed'),
        ]
      }
    },
    lineChartData: function () {
      let labels = []
      let datasets_news = {
        lineTension: 0,
        label: this.$t('dashboard.category.new'),
        borderColor: "#033e8c",
        pointBackgroundColor: "white",
        borderWidth: 2,
        fill: true,
        pointBorderColor: "#033e8c",
        data: [],
      }

      let dataset_processing = {
        lineTension: 0,
        label: this.$t('dashboard.category.processing'),
        borderColor: "Green",
        pointBackgroundColor: "white",
        borderWidth: 2,
        fill: true,
        pointBorderColor: "Green",
        data: [],
      }

      let dataset_processed = {
        lineTension: 0,
        label: this.$t('dashboard.category.processed'),
        borderColor: "#d9753b",
        pointBackgroundColor: "white",
        borderWidth: 2,
        fill: true,
        pointBorderColor: "#d9753b",
        data: [],
      }

      this.carStatus.forEach(e => {
        labels.push(e.date)

        datasets_news.data.push(parseInt(e.NEW))
        dataset_processing.data.push(parseInt(e.PROCESSING))
        dataset_processed.data.push(parseInt(e.PROCESSED))
      })

      const data = {
        labels: labels,
        datasets: [datasets_news, dataset_processing, dataset_processed],
      }

      return data
    },
    lineChartOptions: function () {
      return {
        scales: {
          yAxes: [
            {            
              ticks: {
                beginAtZero: true,
                stepSize: this.stepSize,
                fontColor: "#b0bac9",
                fontFamily: "Rubik",
                fontSize: 15,
              },
              gridLines: {
                display: true,
                borderDashOffset: 0.3
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: true,
                borderDashOffset: 0.3
              },
            },
          ],
        },
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 10,
            fontSize: 15,
            fontFamily: "Rubik",
            fontColor: "#2e384d",
            usePointStyle: true,
            padding: 30,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      }
    },
  },
  watch: {
    calendarVisible: function (val) {
      if (!val && this.dates.length == 2) {
        this.fetchCarStatus(this.dates[0], this.dates[1])
      }
    }
  },
  methods: {
    startNewProcess(definitionKey) {
      this.$store.dispatch('retrieveProcessInstanceID', definitionKey)
      .then(response => {
        console.log("New process instance ID:", response)
      })
      .catch(error => {
        console.log("Cannot start new process:", error)
        this.$store.dispatch('showNotification', {
          text: 'errorWhileStartingProcess',
          interval: 4000})
      })
    },
    showOrHideProcessMenu() {
      this.menuVisible = !this.menuVisible
      if (this.menuVisible) {
        this.previousScrollY = window.scrollY
        // window.addEventListener('scroll', this.noScroll);
      } else {
        // window.removeEventListener('scroll', this.noScroll);
      }
    },
    hideProcessMenu() {
      this.menuVisible = false
      window.removeEventListener('scroll', this.noScroll);
    },
    noScroll() {
      window.scrollTo(0, this.previousScrollY)
    },
    fetchDeployedProcessList() {
      this.$store.dispatch('retrieveProcessDefinitionList')
      .then(response => {
        console.log("Success in retrieving deployed process list", response)
      })
      .catch(error => {
        console.log("Failed retrieving deployed process list", error)
      })
    },
    fetchCarStatus(startDate, endDate) {
      this.isLoadingMetrics = true
      const url = `/Metrics/cars/status?start_date=${startDate}&end_date=${endDate}`

      this.axios.get(url, {}, { withCredentials: true })
      .then(response => {
        this.carStatus = response.data.data

        let values = []
        this.carStatus.forEach(e => {
          values.push(parseInt(e.NEW))
          values.push(parseInt(e.PROCESSING))
          values.push(parseInt(e.PROCESSED))
        })

        const peak = Math.floor(Math.max(values) / 5)

        if (!peak || peak == 0) {
          this.stepSize = 1
        } else {
          this.stepSize = peak
        }
        console.log("Step size", this.stepSize)
        if (values.length == 0) {
          this.plausible = false
        } else {
          const validate_ = (element) => element > 0
          this.plausible = values.some(validate_)
        }
      })
      .catch(error => {
        this.carStatus = []
        this.plausible = false
        console.log("Failed retrieving car status", error)
      })
      .finally(() => {
        this.isLoadingMetrics = false
      })
    },
    carCountsByCategory () {
      if (this.carStatus.length == 0)
        return []

      let newCars = 0, processing = 0, processed = 0

      this.carStatus.forEach(e => {
        newCars += parseInt(e.NEW)
        processing += parseInt(e.PROCESSING)
        processed += parseInt(e.PROCESSED)
      })

      this.carsRegistered = newCars
      this.carsWaitingProcessing = processing
      this.carsProcessed = processed

      return [newCars, processing, processed]
    },
    convertDate2String(date) {
      /* td 202009071750 quick fix for dates that return 32 as the day (#115) */
      return date.getFullYear() + '-' + ("0" + (date.getMonth() + 1)).slice(-2) + '-' + ("0" + (date.getDate())).slice(-2)
    }
  },
  created() {
    const d = new Date();
    const endDateString = this.convertDate2String(d)

    d.setDate(d.getDate() - 7)
    const startDateString = this.convertDate2String(d)
    console.log(d)
    console.log(startDateString)
    this.dates = [startDateString, endDateString]

    this.fetchDeployedProcessList()
    this.fetchCarStatus(startDateString, endDateString)
  }
}
</script>

<style scoped>
.blur {
  filter: blur(5px);
  -webkit-filter: blur(5px);
  opacity: 0.75;
  pointer-events: none;
}

.v-menu__content{
  box-shadow: none;
  width: 95% !important;
  height: 75%;
}

.bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  overflow-y: auto;
}

.btn-animation {
  transform: rotate(45deg);
}

.table-badge {
  font-size: 0.75rem;
  height: 1.25rem;
  border-radius: 0.625rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

.process-name {
  font-size: 1.125rem;
  font-weight: 600;
}

.chart-status {
  height: 250px !important;
}

.chart-statistics {
  height: 12.5rem !important;
}

ul {
  list-style-type: none;
  width: 100%;
}
</style>